import { Box, Grid, Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React from "react";
import OptionSwitch from "./../formElements/OptionSwitch";
import pageStyles from "./../../pages/pageStyles";
import { Bin as ProcessorBin } from "../../store/processor/types";
import { Bin } from "../../store/card/types";
import { RangeType, PromptType } from "../../store/cardPrompt/types";
import ItemDetailAdditionalData from "./ItemDetailAdditionalData";
import ItemDetailBinData from "./ItemDetailBinData";
import ItemDetailuserGroupData from "./ItemDetailuserGroupData";
import ItemDetailBinRange from "./ItemDetailBinRange";
import ItemDetailBinPrompt from "./ItemDetailBinPrompt";
import OptionalToolTip from "../toolTip/OptionalToolTip";
import SectionReadForbidden from "../sectionAccess/SectionReadForbidden";
import ItemDetailBinDataProcessor from "./ItemDetailBinDataProcessor";

export type Item = {
  fieldName: string;
  fieldValue?: string | string[] | boolean | number;
  isImg?: boolean;
  isCode?: boolean;
  additionalData?: AdditionalData[];
  bin?: Bin[];
  binProcesors?: ProcessorBin[];
  tooltip?: string;
};

export type AdditionalData = {
  Encrypted?: boolean;
  Tag?: string;
  Value?: string;
};

export interface ItemDetail {
  sectionName: string;
  itemData?: Item[];
  isChild?: boolean;
  isColumn?: boolean;
  lastItem?: boolean;
  isRTL?: boolean;
  additionalData?: AdditionalData[];
  bin?: Bin[];
  binProcesors?: ProcessorBin[];
  userGroup?: any;
  binRange?: RangeType[];
  binPrompt?: PromptType[];
  isRestricted?: boolean;
  isSurcharge?: boolean;
}

const ItemDetailViewSection = (props: ItemDetail) => {
  const {
    sectionName,
    itemData,
    isChild,
    isColumn = false,
    lastItem = false,
    isRTL = false,
    additionalData,
    bin,
    binProcesors,
    userGroup,
    binRange,
    binPrompt,
    isRestricted,
    isSurcharge = false,
  } = props;

  const useStyles = makeStyles(() => ({
    boxWrapper: {
      marginBottom: lastItem ? "0" : "50px",
      boxShadow: "0 2px 1px rgb(0 0 0 / 5%), 0 0 1px rgb(0 0 0 / 45%)",
    },
    contentWrapper: {
      padding: "15px",
      display: isColumn ? "flex" : "",
      flexWrap: isColumn ? "wrap" : "nowrap",
      border: "1px solid var(--dmg-gray-200)",
      borderTop: "none",
      borderBottom: "none",
    },
    contentCol: {
      width: isColumn ? "50%" : "",
    },
    contentBox: {
      display: "flex",
      alignItems: "center",
      flexDirection: isRTL ? "row-reverse" : "unset",
      padding: 8,
    },
    contentNone: {
      background: "var(--dmg-gray-300)",
      padding: "2px 10px",
      color: "var(--dmg-gray-700)",
      fontSize: "12px",
      borderRadius: "15px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "44px",
      marginLeft: isRTL ? "8px" : "0",
    },
    textTitle: {
      marginLeft: isRTL ? "20px" : "0",
      paddingBottom: "5px",
    },
    devide: {
      borderTop: "1px solid var(--dmg-gray-200)",
    },
  }));

  const classes = pageStyles();
  const styles = useStyles();

  const booleanArray = ["yes", "no", "enable", "disabled", "true", "false"];

  const isBooleanValue = (value: string) =>
    booleanArray.includes(value.toLowerCase());

  const getBooleanValue = (value: string): boolean => {
    const lowerCaseValue = value.toLowerCase();
    return (
      isBooleanValue(lowerCaseValue) &&
      (lowerCaseValue === "yes" ||
        lowerCaseValue === "enable" ||
        lowerCaseValue === "true")
    );
  };

  const displayOptionValue = (item: Item) => {
    if (item.isImg) {
      return <img src={item.fieldValue as string} className={classes.isImg} />;
    } else if (item.isCode) {
      return <pre className={classes.isCode}>{item.fieldValue}</pre>;
    } else if (
      typeof item.fieldValue === "boolean" ||
      (typeof item.fieldValue === "string" && isBooleanValue(item.fieldValue))
    ) {
      return (
        <OptionSwitch
          isDisabled={true}
          isDefaultChecked={
            typeof item.fieldValue === "boolean"
              ? item.fieldValue
              : getBooleanValue(item.fieldValue)
          }
          isRTL={isRTL}
        />
      );
    } else if (
      Array.isArray(item.fieldValue) &&
      item.fieldValue.some((i) => typeof i == "string")
    ) {
      if (item.fieldName === 'Pans') {
        return (
          <pre>{item.fieldValue.join("\n")}</pre>
        )
      }
      return item.fieldValue.join(", ");
    } else {
      return (
        <Typography className={item.fieldValue ? "" : styles.contentNone}>
          {item.fieldValue || "N/A"}
        </Typography>
      );
    }
  };

  // return the correct column value for fieldName, fieldValue col
  const fieldNameCol = isColumn ? (isRTL ? 11 : 4) : isSurcharge ? 6 : 2;
  const fieldValueCol = isColumn ? (isRTL ? 1 : 8) : isSurcharge ? 6 : 10;

  return (
    <Paper
      elevation={0}
      className={isChild ? classes.sectionWrapperChild : styles.boxWrapper}
    >
      <Grid
        item
        xs={12}
        sm
        className={
          isChild ? classes.sectionHeadingChild : classes.sectionHeading
        }
      >
        <Typography
          variant="h5"
          display="block"
          className={
            isChild
              ? classes.sectionHeadingTitleChild
              : classes.sectionHeadingTitle
          }
        >
          {sectionName}
        </Typography>
      </Grid>
      {!isRestricted ? (
        <Box className={styles.contentWrapper}>
          {itemData && !itemData.length
            ? "No Data"
            : itemData &&
              itemData.map((item, idx) => (
                <Grid
                  key={idx}
                  container
                  spacing={2}
                  className={styles.contentCol}
                  // style={{ display: item.fieldValue === "" ? "none" : "" }} // if value is empty string, hide it
                >
                  <Grid item sm container className={styles.contentBox}>
                    <Grid item xs={6} md={fieldNameCol}>
                      <OptionalToolTip tooltip={item.tooltip || null}>
                        <Typography
                          display="block"
                          className={styles.textTitle}
                        >
                          {item.fieldName}
                          {isRTL ? "" : ":"}
                        </Typography>
                      </OptionalToolTip>
                    </Grid>
                    <Grid item xs={6} md={fieldValueCol}>
                      {displayOptionValue(item)}
                    </Grid>
                  </Grid>
                </Grid>
              ))}

          {additionalData?.length ? (
            <ItemDetailAdditionalData
              additionalData={additionalData}
              styles={styles}
            />
          ) : null}

          {bin?.length ? <ItemDetailBinData bin={bin} styles={styles} /> : null}

          {binProcesors?.length ? (
            <ItemDetailBinDataProcessor bin={binProcesors} styles={styles} />
          ) : null}

          {userGroup ? <ItemDetailuserGroupData userGroup={userGroup} /> : null}

          {binRange?.length ? (
            <ItemDetailBinRange binRange={binRange} styles={styles} />
          ) : null}

          {binPrompt?.length ? (
            <ItemDetailBinPrompt binPrompt={binPrompt} styles={styles} />
          ) : null}
        </Box>
      ) : (
        <SectionReadForbidden />
      )}
    </Paper>
  );
};

export default ItemDetailViewSection;
